'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';

import { SearchEngineContainer } from '@nocowanie/common-ui/components/organisms/search-engine-container';
import { useSearchCookie, useThrottledCallback } from '@nocowanie/common-ui/hooks';
import { SearchEngineModel } from '@nocowanie/common-ui/models';

import { commonUiStateSlice, useAppSelector } from '@app/app-state';
import { environment } from '@app/environments/environment';
import {
    extractGeoIdAndTypeFromAutocompleteItem,
    GeoLocationData,
} from '@app/helpers/autocomplete.helpers';
import { useMapAutocompleteData } from '@app/hooks/useMapAutocompleteData';
import { useSearch } from '@app/hooks/useSearch';
import { AutocompleteItemModel } from '@app/models/elastic-search';

import { resolveFormDates, resolveFormFlexDates } from './listing-search-bar.helpers';
import { ListingSearchBarProps } from './listing-search-bar.props';
import { useForceDatesSelection } from './useForceDatesSelection';
import { useSearchEngineSubmit } from './useSearchEngineSubmit';

export const ListingSearchBar = ({
    cmsCheckIn,
    cmsCheckOut,
    cmsFlexDates,
    locationPlaceholder,
    ...rest
}: ListingSearchBarProps) => {
    const mobileDrawerDisclosure = useDisclosure();
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const { searchCriteria } = useSearch();
    const { setCookie, getSearchCriteriaFromCookie } = useSearchCookie(environment.COOKIE_DOMAIN);

    const lastSelectedLocationRef = useRef<GeoLocationData | null>(null);

    const [cookieData, setCookieData] = useState<Partial<SearchEngineModel> | null>(null);
    const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);

    useEffect(() => {
        const cookieValue = getSearchCriteriaFromCookie();
        setCookieData(cookieValue || null);
        setIsInitialDataLoaded(true);
    }, [getSearchCriteriaFromCookie]);

    // Single source of truth for form data with priority: URL (searchCriteria) > cookie > CMS > defaults
    const initialSearchFormData = useMemo(() => {
        const dates = resolveFormDates({
            isInitialDataLoaded,
            checkInDate: searchCriteria.checkInDate,
            checkOutDate: searchCriteria.checkOutDate,
            cookieCheckInDate: cookieData?.dates?.checkInDate,
            cookieCheckOutDate: cookieData?.dates?.checkOutDate,
            cmsCheckIn,
            cmsCheckOut,
        });

        const flexDates = resolveFormFlexDates({
            urlFlexDates: searchCriteria.flexDates,
            cookieFlexDates: cookieData?.flexDates,
            cmsFlexDates,
        });

        return {
            location: searchCriteria.location || '',
            geoId: searchCriteria.geoId || '',
            dates,
            guests: {
                adultsCount: searchCriteria.checkInDate
                    ? searchCriteria.guests.adultsCount
                    : (cookieData?.guests?.adultsCount ?? searchCriteria.guests.adultsCount),
                childrenCount: searchCriteria.checkInDate
                    ? searchCriteria.guests.childrenCount
                    : (cookieData?.guests?.childrenCount ?? searchCriteria.guests.childrenCount),
                childrenAges: searchCriteria.checkInDate
                    ? searchCriteria.guests.childrenAges
                    : (cookieData?.guests?.childrenAges ?? searchCriteria.guests.childrenAges),
            },
            flexDates,
        };
    }, [searchCriteria, cmsCheckIn, cmsCheckOut, cmsFlexDates, cookieData, isInitialDataLoaded]);

    const throttledOnLocationSelected = useThrottledCallback((item: AutocompleteItemModel) => {
        lastSelectedLocationRef.current = extractGeoIdAndTypeFromAutocompleteItem(item);
    }, 100);

    const autocompleteData = useMapAutocompleteData({
        initialQuery: searchCriteria?.location || '',
        onAccommodationSelected: useCallback(() => {
            mobileDrawerDisclosure.onClose();
        }, [mobileDrawerDisclosure]),
        onLocationSelected: throttledOnLocationSelected,
    });

    const { items, handleItemSelection } = autocompleteData;

    const itemsRef = useRef(items);
    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    const onSearchEngineSubmit = useSearchEngineSubmit({
        lastSelectedLocationRef,
        itemsRef,
        handleItemSelection,
        setCookie,
        getSearchCriteriaFromCookie,
        onClose: mobileDrawerDisclosure.onClose,
    });

    const { forceDatesSelection, mobileTriggerWrapper } = useForceDatesSelection({
        isInitialDataLoaded,
        dates: initialSearchFormData.dates,
        isDrawerOpen: mobileDrawerDisclosure.open,
    });

    return (
        <SearchEngineContainer
            initialFormData={initialSearchFormData}
            onSearchEngineSubmit={onSearchEngineSubmit}
            isV7SearchEngineVersion
            isMobileCompactVersion={false}
            isMobileBrowser={isMobileBrowser}
            autocompleteData={autocompleteData}
            mobileDrawerDisclosure={mobileDrawerDisclosure}
            hideInstantReservationInput
            withListing
            locationPlaceholder={locationPlaceholder}
            forceDatesSelection={forceDatesSelection}
            mobileTriggerWrapper={mobileTriggerWrapper}
            {...rest}
        />
    );
};
